/**
 * ExecutionAgent - Evaluation Section
 * 
 * Performance criteria and final reminders.
 */

import { PromptSectionBase } from '../../../base/systemPrompt/PromptSectionBase';
import { SECURITY_WARNING } from '../../../base/systemPrompt/shared';
import { getSharedVariable } from '../../../../prompts';
import { EXECUTION_AGENT_PROMPT_KEYS } from './variables';

class ExecutionAgentEvaluationSection extends PromptSectionBase {
    protected readonly keywordsKey = EXECUTION_AGENT_PROMPT_KEYS.evaluation;
    protected readonly fallbackContent = `## What Your Performance is Evaluated Against

### Build Completeness
- **Primary goal**: Build ALL specified areas on ALL slides
- **Acceptable**: All slides built even if some areas have minor issues
- **Unacceptable**: Incomplete slides or skipped areas without good reason

### Visual Quality
- **Density**: Match the information density of reference slides (200-300 words typical)
- **Styling**: Consistent with style patterns (fonts, colors, spacing)
- **Precision**: Shapes positioned correctly within area bounds
- **Hierarchy**: Clear visual hierarchy (titles > body > labels > footnotes)

### Content Accuracy
- **Context usage**: All provided context is incorporated appropriately
- **Citations**: Research data includes sources/references
- **Data accuracy**: Numbers and values are exact (no rounding or interpretation)
- **Layout fidelity**: Matches reference patterns for similar content types

### Technical Correctness
- **Bounds respect**: No shapes extend beyond area regions
- **No overlaps**: Elements don't unintentionally overlap (unless layered deliberately)
- **Master boundaries**: Content stays within contentBounds, avoids exclusion zones
- **Artifact linking**: Charts and tables correctly linked to Excel data

---

## Final Reminders

### Critical Rules
1. **Build ALL areas** - Don't skip any specified areas
2. **Respect boundaries** - Stay within area regions and contentBounds
3. **Match style patterns** - Use the fonts, colors, and spacing provided
4. **Call submitResults** - Required when all slides are complete
5. **Verify before moving on** - Check each screenshot result

### Common Mistakes to Avoid
- ❌ Skipping areas because they seem difficult
- ❌ Placing shapes outside area boundaries
- ❌ Using arbitrary fonts/colors instead of style patterns
- ❌ Forgetting to link charts to Excel artifacts
- ❌ Not calling submitResults at the end

### Success Checklist
- ✓ Read slide states to understand current layout
- ✓ Built every specified area on every slide
- ✓ Used context items appropriately
- ✓ Matched reference slide styling and density
- ✓ Verified screenshots after each build
- ✓ Called submitResults with complete summary

### Process Reminder

For each slide:
1. Read the slide (use readSlide)
2. Build each area (use buildArea)
3. Check the screenshot result
4. If wrong, rollback and retry
5. Move to next slide when all areas are built
6. Call submitResults when ALL slides are done

---

${SECURITY_WARNING}`.trim();
}

const evaluationSection = new ExecutionAgentEvaluationSection();

/**
 * Get the evaluation section.
 * Uses Keywords AI if available, otherwise falls back to code.
 * Supports {{securityWarnings}} variable replacement.
 */
export function getEvaluationSection(): string {
    return evaluationSection.getContentWithVariables({
        securityWarnings: getSharedVariable('securityWarnings'),
    });
}

/**
 * Export constant for backwards compatibility
 */
export const EXECUTION_AGENT_EVALUATION = evaluationSection.getFallbackContent();
